import React, { useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { GroupAddOutlined } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { useToast } from '../contexts/ToastContext';
import { useActingStoreAdmin } from '../hooks/useActingStoreAdmin';
import { EmptyState, FormDialog, SectionCard } from './ui';

interface JoinRequest {
  id: string;
  userId: string;
  message?: string | null;
  requesterName?: string | null;
  requesterEmail?: string | null;
  createdAt?: unknown;
}

const JOIN_REQUESTS_QUERY_KEY = 'store-join-requests';

/**
 * People asking to join this store. Only an owner or admin sees or acts on
 * this — same check the API makes, so the card just doesn't render for
 * anyone else. Absent when there is nothing pending.
 */
export const OrganizationJoinRequestsCard: React.FC = () => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { canManage: canReview, organizationId: actingOrganizationId } = useActingStoreAdmin();
  const [rejectTarget, setRejectTarget] = useState<JoinRequest | null>(null);
  const [rejectNotes, setRejectNotes] = useState('');

  const { data: requests = [], isLoading, isError } = useQuery<JoinRequest[]>({
    queryKey: [JOIN_REQUESTS_QUERY_KEY, actingOrganizationId],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/organizations/${actingOrganizationId}/join-requests`, { headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.error?.message || body?.message || t('store.joinRequests.loadFailed'));
      }
      return (body?.data as JoinRequest[]) || [];
    },
    enabled: canReview && Boolean(actingOrganizationId),
    staleTime: 60_000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [JOIN_REQUESTS_QUERY_KEY, actingOrganizationId] });
  };

  const approve = useMutation({
    mutationFn: async (requestId: string) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/organizations/join-requests/${requestId}/approve`, {
        method: 'POST',
        headers,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.error?.message || body?.message || t('store.joinRequests.approveFailed'));
      }
    },
    onSuccess: () => {
      showToast(t('store.joinRequests.approved'), 'success');
      invalidate();
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const reject = useMutation({
    mutationFn: async ({ requestId, notes }: { requestId: string; notes: string }) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/organizations/join-requests/${requestId}/reject`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ notes: notes || undefined }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.error?.message || body?.message || t('store.joinRequests.rejectFailed'));
      }
    },
    onSuccess: () => {
      showToast(t('store.joinRequests.rejected'), 'success');
      setRejectTarget(null);
      setRejectNotes('');
      invalidate();
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  if (!canReview) return null;
  if (!isLoading && !isError && requests.length === 0) return null;

  return (
    <SectionCard
      title={t('store.joinRequests.title')}
      subtitle={t('store.joinRequests.description')}
      leading={<GroupAddOutlined color="primary" />}
    >
      {isError && (
        <EmptyState size="compact" title={t('store.joinRequests.loadFailed')} />
      )}

      {!isError && (
        <Stack spacing={2} divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
          {requests.map((req) => (
            <Box key={req.id}>
              <Typography variant="subtitle2">
                {req.requesterName || req.userId}
              </Typography>
              {req.requesterEmail && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {req.requesterEmail}
                </Typography>
              )}
              {req.message && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {req.message}
                </Typography>
              )}
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={approve.isPending}
                  onClick={() => approve.mutate(req.id)}
                >
                  {t('store.joinRequests.approve')}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={reject.isPending}
                  onClick={() => {
                    setRejectTarget(req);
                    setRejectNotes('');
                  }}
                >
                  {t('store.joinRequests.reject')}
                </Button>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      <FormDialog
        open={Boolean(rejectTarget)}
        title={t('store.joinRequests.rejectDialogTitle')}
        onClose={() => {
          if (reject.isPending) return;
          setRejectTarget(null);
        }}
        onSubmit={() => {
          if (!rejectTarget) return;
          reject.mutate({ requestId: rejectTarget.id, notes: rejectNotes });
        }}
        submitLabel={t('store.joinRequests.reject')}
        submitColor="error"
        submitDisabled={reject.isPending}
      >
        <Box sx={{ pt: 1 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={t('store.joinRequests.rejectNotesLabel')}
            placeholder={t('store.joinRequests.rejectNotesPlaceholder')}
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            disabled={reject.isPending}
          />
        </Box>
      </FormDialog>
    </SectionCard>
  );
};

export default OrganizationJoinRequestsCard;
